import React, { useEffect } from 'react'

import { useVirtualizer } from '@tanstack/react-virtual'
import Select, {
  components as RSComponents,
  type FilterOptionOption,
  type MenuListProps,
  type MenuPlacement,
  type OptionProps,
  type SingleValueProps,
} from 'react-select'

import { EmotionProvider } from '@/providers'

import { cx } from '../../ui/utils'

// const Select = React.lazy(() => import('react-select'))

export interface DropdownOption {
  value: string
  label: string
  /** Optional richer display string that extends the label (e.g. a company's name plus its parent
   *  hierarchy, "Bega NSW (Bega/Linfox FMCG)"). Shown inline in the menu and searchable; on the
   *  selected value it appears only as a hover tooltip. Expected to start with `label`. */
  fullName?: string
  /** Optional marker shown before the label, in the menu and on the selected value. */
  icon?: React.ReactNode
}

// Show the fullName inline in the menu options (so the hierarchy is visible and searchable), with
// the parenthetical part muted and a hover tooltip.
const HierarchyOption = (props: OptionProps<DropdownOption>) => {
  const { label, fullName, icon } = props.data
  const extra = fullName && fullName.startsWith(label) ? fullName.slice(label.length) : ''
  return (
    <RSComponents.Option {...props}>
      <span title={fullName || undefined} className={icon ? 'inline-flex items-center gap-2' : undefined}>
        {icon}
        {label}
        {extra ? <span className="text-xs opacity-60">{extra}</span> : null}
      </span>
    </RSComponents.Option>
  )
}

// On the selected value, show only the label but expose the fullName as a hover tooltip.
const HierarchySingleValue = (props: SingleValueProps<DropdownOption>) => (
  <RSComponents.SingleValue {...props}>
    <span
      title={props.data.fullName || undefined}
      className={props.data.icon ? 'inline-flex items-center gap-2' : undefined}
    >
      {props.data.icon}
      {props.data.label}
    </span>
  </RSComponents.SingleValue>
)

type DropdownValueType = DropdownOption | DropdownOption[] | null

/** Above this many options the menu renders only the rows in view. */
const VIRTUALIZE_ABOVE = 200
const VIRTUAL_ROW_HEIGHT = 42

/**
 * A menu list that renders only the visible options. react-select otherwise mounts every
 * option when the menu opens, which freezes the browser for a company with thousands of
 * assets. Keyboard navigation still works: the focused option is scrolled into view here,
 * since react-select's own scrolling can't reach rows that aren't mounted.
 */
const VirtualMenuList = (props: MenuListProps<DropdownOption, boolean>) => {
  const rows = React.Children.toArray(props.children)
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => VIRTUAL_ROW_HEIGHT,
    overscan: 8,
  })
  const focusedIndex = rows.findIndex(
    (row) => React.isValidElement<{ isFocused?: boolean }>(row) && row.props.isFocused === true
  )
  React.useEffect(() => {
    if (focusedIndex >= 0) virtualizer.scrollToIndex(focusedIndex)
  }, [focusedIndex, virtualizer])

  return (
    <div ref={scrollRef} style={{ maxHeight: props.maxHeight, overflowY: 'auto' }} className="z-30">
      <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
        {virtualizer.getVirtualItems().map((item) => (
          <div
            key={item.key}
            data-index={item.index}
            ref={virtualizer.measureElement}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', transform: `translateY(${item.start}px)` }}
          >
            {rows[item.index]}
          </div>
        ))}
      </div>
    </div>
  )
}

const BASE_COMPONENTS = { Option: HierarchyOption, SingleValue: HierarchySingleValue }
const VIRTUAL_COMPONENTS = { ...BASE_COMPONENTS, MenuList: VirtualMenuList }

// Note: do not be tempted to add a defaultValue to this.
// Just initialize your thing with the values set so value
// starts out as the default; defaultValue is for uncontrolled
// so you don't want both a value and a defaultValue. We're using
// controlled only.
export interface DropdownInterface {
  options: DropdownOption[]
  placeholder?: string
  isMultiSelect?: boolean
  selectedItem: DropdownValueType
  handleSingleSelectItem?: (value: DropdownOption | null) => void
  handleMultiSelectItem?: (value: DropdownOption[]) => void
  id?: string
  label?: string
  animateDots?: boolean
  maxVisible?: number
  optionHeight?: number // sensible default but if you have a weird small or large dropdown you can adjust
  includeSelectAll?: boolean
  isClearable?: boolean
  noOptionsMessage?: string
  /** Menu open direction. Defaults to 'auto' (react-select picks based on space);
   *  set 'top' for dropdowns low in a tall panel so the menu doesn't open off-screen. */
  menuPlacement?: MenuPlacement
}

const filterOptions = (candidate: FilterOptionOption<DropdownOption>, input: string) => {
  if (input) {
    const c = (candidate.data?.fullName || candidate.label).replace(/-/g, '').toLowerCase()
    const i = input.replace(/-/g, '').toLowerCase()
    return c.includes(i)
  }
  return true
}

export default function Dropdown({
  options,
  selectedItem,
  handleSingleSelectItem,
  handleMultiSelectItem,
  placeholder = 'Select...',
  isMultiSelect = false,
  id,
  label,
  animateDots = false,
  includeSelectAll = false,
  isClearable = true,
  maxVisible = 10,
  optionHeight = 42,
  noOptionsMessage = 'No records found',
  menuPlacement = 'auto',
}: DropdownInterface) {
  const controlRef = React.useRef<HTMLDivElement>(null)
  const [animatedPh, setAnimatedPh] = React.useState<string | undefined>(placeholder)

  useEffect(() => {
    if (animateDots) {
      let dots = 0
      const interval = setInterval(() => {
        dots = (dots + 1) % 4 // 0, 1, 2, 3
        const ph = `${placeholder.replace(/\.\.\.$/, '')}${'.'.repeat(dots)}`
        setAnimatedPh(ph)
      }, 500)
      return () => {
        clearInterval(interval)
      }
    } else {
      setAnimatedPh(undefined)
    }
  }, [placeholder, animateDots])

  useEffect(() => {}, [])

  const effectiveOptions =
    isMultiSelect && includeSelectAll
      ? [...options.filter((o) => !o.value), { value: '*', label: 'Select All' }, ...options.filter((o) => !!o.value)]
      : options

  const handleSingleSelectChange = (value: DropdownValueType) => {
    // Have to handle null on singleSelect
    if (!isMultiSelect && !Array.isArray(value)) {
      handleSingleSelectItem?.(value)
    }
  }

  const handleMultiSelectChange = (value: DropdownValueType) => {
    if (isMultiSelect && Array.isArray(value)) {
      handleMultiSelectItem?.(value)
    }
  }

  return (
    <EmotionProvider>
      <div ref={controlRef}>
        <Select
          inputId={id}
          isClearable={isClearable}
          isMulti={Boolean(isMultiSelect)}
          aria-label={label}
          options={effectiveOptions}
          placeholder={animatedPh || placeholder}
          components={options.length > VIRTUALIZE_ABOVE ? VIRTUAL_COMPONENTS : BASE_COMPONENTS}
          filterOption={filterOptions}
          noOptionsMessage={() => noOptionsMessage}
          maxMenuHeight={options.length > maxVisible ? maxVisible * optionHeight : undefined}
          minMenuHeight={Math.min(options.length, 4) * optionHeight}
          classNames={{
            option: ({ isFocused, isSelected }) =>
              cx(
                'px-4 py-2 !cursor-pointer z-30',
                isFocused && '!bg-base-300',
                isSelected && '!bg-base-300 !text-base-content'
              ),
            dropdownIndicator: ({ isFocused }) => cx('px-4 py-2  !cursor-pointer ', isFocused && 'text-base-content'),
            control: ({ isFocused, menuIsOpen }) =>
              cx(
                '!border-base-300 !cursor-pointer',
                isFocused && '!border-base-300 !shadow-none',
                menuIsOpen && '!border-base-300'
              ),
            multiValue: () => cx('!bg-primary !text-primary-content !rounded-sm '),
            multiValueLabel: () => cx('!text-primary-content'),
            multiValueRemove: () => cx('!text-primary-content !rounded-sm hover:!bg-error cursor-pointer'),
            menuList: () => 'overflow-y-auto z-30',
          }}
          menuPosition="fixed"
          menuPlacement={menuPlacement}
          menuPortalTarget={document.body}
          styles={{
            option: (base, props) => {
              // Style select all differently
              if ('value' in props && props.value === '*') {
                return { ...base, fontWeight: 'bold', textDecoration: 'underline' }
              }
              return base
            },
            menu: (base) => ({
              ...base,
              zIndex: 9999,
            }),
            menuPortal: (base) => ({
              ...base,
              zIndex: 9999,
            }),
            menuList: (base) => {
              // Reserve at least one row so the "no options" message isn't clipped to 0px height.
              // Normal sizing now comes from maxMenuHeight/minMenuHeight, so base.maxHeight is
              // usually a real value that passes through untouched; this only kicks in when it
              // would otherwise collapse to 0.
              return {
                ...base,
                maxHeight: base.maxHeight ? base.maxHeight : `${optionHeight}px`,
                overflowY: 'auto',
                zIndex: 30,
              }
            },
          }}
          value={selectedItem}
          onChange={(value) => {
            if (isMultiSelect) {
              const selected = (value as DropdownOption[]) ?? []
              if (selected.some((o) => o.value === '*')) {
                handleMultiSelectChange(options.filter((o) => !!o.value && o.value !== '**'))
              } else {
                handleMultiSelectChange(value as DropdownOption[])
              }
            } else {
              handleSingleSelectChange(value as DropdownOption)
            }
          }}
        />
      </div>
    </EmotionProvider>
  )
}
